// Hand-written STEP AP214 exporter.
//
// The panel is built the robust way: one planar region whose outer wire is
// the boundary and whose inner wires are the (pre-unioned, disjoint) holes,
// extruded once. No boolean operations are involved at all; the exporter
// writes the B-rep of the extrusion directly.
//
// Circular holes and circular boundaries that survive the pipeline untouched
// are written as analytic circles with cylindrical side walls, so CAD
// packages see exact geometry. Everything else (slots, polygons, stars,
// unioned clusters, clipped shapes) is written as polyline wires with planar
// side walls.
//
// Orientation bookkeeping (verified by edge pairing: in the closed shell
// every edge is referenced exactly twice with opposite sense):
//   - rings are normalized outer CCW, holes CW (algebraic winding)
//   - bottom face: plane with axis +z, ADVANCED_FACE same_sense .F.,
//     all loops traverse rings reversed
//   - top face: plane at z = thickness, same_sense .T., loops as stored
//   - polyline side faces: plane with axis = outward normal, loop
//     [bottom fwd, vertical up, top rev, vertical down]
//   - cylinder side faces: hole cylinders same_sense .F. (normal points into
//     the bore), boundary cylinder .T.

import type { CircleInfo, GenResult, Poly, Ring } from '../core/types'
import { panelGeometry, splitPanelPolys } from '../core/pipeline'
import { centroid, ringMatchesCircle } from '../core/geometry'

function fmt(x: number): string {
  const v = Math.round(x * 1e6) / 1e6
  let s = String(v)
  if (!s.includes('.') && !s.includes('e') && !s.includes('E')) s += '.'
  return s
}

class StepFile {
  private lines: string[] = []
  private id = 0

  add(content: string): number {
    this.id++
    this.lines.push(`#${this.id}=${content};`)
    return this.id
  }

  body(): string {
    return this.lines.join('\n')
  }
}

interface RingCircle {
  kind: 'circle'
  circle: CircleInfo
  isOuter: boolean
}

interface RingPoly {
  kind: 'poly'
  pts: Ring
  isOuter: boolean
}

type RingGeom = RingCircle | RingPoly

interface RingEntities {
  geom: RingGeom
  // Polyline rings: per-vertex and per-segment entities.
  bottomEdges: number[]
  topEdges: number[]
  vertEdges: number[]
  // Circle rings: one closed edge per z level.
  bottomCircleEdge: number
  topCircleEdge: number
}

// Modes: 'panel' writes the plate with holes as inner wires (one prism, our
// moat); 'shapes' writes each hole as its own solid, compounded in one file,
// for CAD workflows that want the cut shapes themselves. In shapes mode a
// hole's analytic circle becomes the exact outer cylinder of its little
// solid. `split` partitions the panel into a grid of aligned sub-panels
// (one solid each, absolute coordinates preserved) so huge patterns open in
// CAD packages that choke on one giant face.
export type StepMode = 'panel' | 'shapes'

export interface StepSplit {
  cols: number
  rows: number
}

export function exportStep(
  result: GenResult,
  thickness: number,
  tolerance: number,
  mode: StepMode = 'panel',
  split?: StepSplit,
): string {
  const eps = Math.max(1e-6, tolerance * 1e-3)
  const name = mode === 'panel' ? 'Panel' : 'Holes'

  const f = new StepFile()

  // Product and context scaffolding.
  const appCtx = f.add(`APPLICATION_CONTEXT('core data for automotive mechanical design processes')`)
  f.add(`APPLICATION_PROTOCOL_DEFINITION('international standard','automotive_design',2010,#${appCtx})`)
  const prodCtx = f.add(`PRODUCT_CONTEXT('',#${appCtx},'mechanical')`)
  const product = f.add(`PRODUCT('${name}','Ventholes ${name.toLowerCase()}','',(#${prodCtx}))`)
  f.add(`PRODUCT_RELATED_PRODUCT_CATEGORY('part','',(#${product}))`)
  const formation = f.add(`PRODUCT_DEFINITION_FORMATION('','',#${product})`)
  const defCtx = f.add(`PRODUCT_DEFINITION_CONTEXT('part definition',#${appCtx},'design')`)
  const prodDef = f.add(`PRODUCT_DEFINITION('design','',#${formation},#${defCtx})`)
  const prodDefShape = f.add(`PRODUCT_DEFINITION_SHAPE('','',#${prodDef})`)

  const lengthUnit = f.add(`(LENGTH_UNIT()NAMED_UNIT(*)SI_UNIT(.MILLI.,.METRE.))`)
  const angleUnit = f.add(`(NAMED_UNIT(*)PLANE_ANGLE_UNIT()SI_UNIT($,.RADIAN.))`)
  const solidAngleUnit = f.add(`(NAMED_UNIT(*)SI_UNIT($,.STERADIAN.)SOLID_ANGLE_UNIT())`)
  const uncertainty = f.add(
    `UNCERTAINTY_MEASURE_WITH_UNIT(LENGTH_MEASURE(1.E-05),#${lengthUnit},'distance_accuracy_value','')`,
  )
  const geomCtx = f.add(
    `(GEOMETRIC_REPRESENTATION_CONTEXT(3)GLOBAL_UNCERTAINTY_ASSIGNED_CONTEXT((#${uncertainty}))GLOBAL_UNIT_ASSIGNED_CONTEXT((#${lengthUnit},#${angleUnit},#${solidAngleUnit}))REPRESENTATION_CONTEXT('',''))`,
  )

  const worldPlacement = axis2Placement(f, 0, 0, 0)

  const solidIds: number[] = []
  if (mode === 'panel') {
    let polys = panelGeometry(result)
    if (split && (split.cols > 1 || split.rows > 1)) {
      polys = splitPanelPolys(polys, result.boundary, split)
    }
    const circles = result.holes.filter((h) => h.circle).map((h) => h.circle as CircleInfo)
    for (const poly of polys) {
      solidIds.push(buildSolid(f, poly, circles, result.boundaryCircle, thickness, eps))
    }
  } else {
    // One solid per hole; the hole's circle (when analytic) plays the role of
    // the outer "boundary" circle of its solid.
    for (const hole of result.holes) {
      solidIds.push(buildSolid(f, hole.poly, [], hole.circle ?? null, thickness, eps))
    }
  }

  const items = [worldPlacement, ...solidIds].map((i) => `#${i}`).join(',')
  const shapeRep = f.add(`ADVANCED_BREP_SHAPE_REPRESENTATION('',(${items}),#${geomCtx})`)
  f.add(`SHAPE_DEFINITION_REPRESENTATION(#${prodDefShape},#${shapeRep})`)

  const now = new Date().toISOString().slice(0, 19)
  return [
    'ISO-10303-21;',
    'HEADER;',
    `FILE_DESCRIPTION(('Ventholes panel'),'2;1');`,
    `FILE_NAME('panel.step','${now}',(''),(''),'Ventholes','Ventholes','');`,
    `FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 0 10303 214 1 1 1 1 }'));`,
    'ENDSEC;',
    'DATA;',
    f.body(),
    'ENDSEC;',
    'END-ISO-10303-21;',
    '',
  ].join('\n')
}

function axis2Placement(
  f: StepFile,
  ox: number,
  oy: number,
  oz: number,
  ax = 0,
  ay = 0,
  az = 1,
  rx = 1,
  ry = 0,
  rz = 0,
): number {
  const origin = f.add(`CARTESIAN_POINT('',(${fmt(ox)},${fmt(oy)},${fmt(oz)}))`)
  const axis = f.add(`DIRECTION('',(${fmt(ax)},${fmt(ay)},${fmt(az)}))`)
  const ref = f.add(`DIRECTION('',(${fmt(rx)},${fmt(ry)},${fmt(rz)}))`)
  return f.add(`AXIS2_PLACEMENT_3D('',#${origin},#${axis},#${ref})`)
}

function classifyRing(
  ring: Ring,
  isOuter: boolean,
  holeCircles: CircleInfo[],
  boundaryCircle: CircleInfo | null,
  eps: number,
): RingGeom {
  const candidates = isOuter ? (boundaryCircle ? [boundaryCircle] : []) : holeCircles
  if (candidates.length > 0) {
    const [cx, cy] = centroid(ring)
    for (const c of candidates) {
      if (Math.hypot(cx - c.cx, cy - c.cy) > c.r * 0.5 + eps) continue
      if (ring.length >= 8 && ringMatchesCircle(ring, c, Math.max(eps, 1e-6))) {
        return { kind: 'circle', circle: c, isOuter }
      }
    }
  }
  return { kind: 'poly', pts: ring, isOuter }
}

function buildSolid(
  f: StepFile,
  poly: Poly,
  holeCircles: CircleInfo[],
  boundaryCircle: CircleInfo | null,
  th: number,
  eps: number,
): number {
  const rings: RingEntities[] = poly.map((ring, i) =>
    buildRingEntities(f, classifyRing(ring, i === 0, holeCircles, boundaryCircle, eps), th),
  )

  const faces: number[] = []

  // Bottom face (outward normal -z): plane axis +z with same_sense .F.,
  // loops traverse every ring reversed.
  {
    const plane = f.add(`PLANE('',#${axis2Placement(f, 0, 0, 0)})`)
    const bounds: string[] = []
    rings.forEach((re, i) => {
      const loop = bottomFaceLoop(f, re)
      const kind = i === 0 ? 'FACE_OUTER_BOUND' : 'FACE_BOUND'
      bounds.push(`#${f.add(`${kind}('',#${loop},.T.)`)}`)
    })
    faces.push(f.add(`ADVANCED_FACE('',(${bounds.join(',')}),#${plane},.F.)`))
  }

  // Top face (outward normal +z): loops as stored.
  {
    const plane = f.add(`PLANE('',#${axis2Placement(f, 0, 0, th)})`)
    const bounds: string[] = []
    rings.forEach((re, i) => {
      const loop = topFaceLoop(f, re)
      const kind = i === 0 ? 'FACE_OUTER_BOUND' : 'FACE_BOUND'
      bounds.push(`#${f.add(`${kind}('',#${loop},.T.)`)}`)
    })
    faces.push(f.add(`ADVANCED_FACE('',(${bounds.join(',')}),#${plane},.T.)`))
  }

  // Side faces.
  for (const re of rings) {
    if (re.geom.kind === 'circle') {
      faces.push(cylinderFace(f, re))
    } else {
      sideFaces(f, re, faces)
    }
  }

  const shell = f.add(`CLOSED_SHELL('',(${faces.map((i) => `#${i}`).join(',')}))`)
  return f.add(`MANIFOLD_SOLID_BREP('',#${shell})`)
}

function buildRingEntities(f: StepFile, geom: RingGeom, th: number): RingEntities {
  const re: RingEntities = {
    geom,
    bottomEdges: [],
    topEdges: [],
    vertEdges: [],
    bottomCircleEdge: 0,
    topCircleEdge: 0,
  }

  if (geom.kind === 'circle') {
    const { cx, cy, r } = geom.circle
    for (const z of [0, th]) {
      const start = f.add(`CARTESIAN_POINT('',(${fmt(cx + r)},${fmt(cy)},${fmt(z)}))`)
      const vertex = f.add(`VERTEX_POINT('',#${start})`)
      const circle = f.add(`CIRCLE('',#${axis2Placement(f, cx, cy, z)},${fmt(r)})`)
      const edge = f.add(`EDGE_CURVE('',#${vertex},#${vertex},#${circle},.T.)`)
      if (z === 0) re.bottomCircleEdge = edge
      else re.topCircleEdge = edge
    }
    return re
  }

  const pts = geom.pts
  const n = pts.length
  const bottomVerts: number[] = []
  const topVerts: number[] = []
  for (const [x, y] of pts) {
    const pb = f.add(`CARTESIAN_POINT('',(${fmt(x)},${fmt(y)},0.))`)
    bottomVerts.push(f.add(`VERTEX_POINT('',#${pb})`))
    const pt = f.add(`CARTESIAN_POINT('',(${fmt(x)},${fmt(y)},${fmt(th)}))`)
    topVerts.push(f.add(`VERTEX_POINT('',#${pt})`))
  }
  const lineEdge = (
    va: number,
    vb: number,
    ax: number,
    ay: number,
    az: number,
    dx: number,
    dy: number,
    dz: number,
  ): number => {
    const len = Math.hypot(dx, dy, dz) || 1
    const p = f.add(`CARTESIAN_POINT('',(${fmt(ax)},${fmt(ay)},${fmt(az)}))`)
    const dir = f.add(`DIRECTION('',(${fmt(dx / len)},${fmt(dy / len)},${fmt(dz / len)}))`)
    const vec = f.add(`VECTOR('',#${dir},1.)`)
    const line = f.add(`LINE('',#${p},#${vec})`)
    return f.add(`EDGE_CURVE('',#${va},#${vb},#${line},.T.)`)
  }
  for (let i = 0; i < n; i++) {
    const [ax, ay] = pts[i]
    const [bx, by] = pts[(i + 1) % n]
    re.bottomEdges.push(lineEdge(bottomVerts[i], bottomVerts[(i + 1) % n], ax, ay, 0, bx - ax, by - ay, 0))
    re.topEdges.push(lineEdge(topVerts[i], topVerts[(i + 1) % n], ax, ay, th, bx - ax, by - ay, 0))
    re.vertEdges.push(lineEdge(bottomVerts[i], topVerts[i], ax, ay, 0, 0, 0, 1))
  }
  return re
}

function orientedEdge(f: StepFile, edge: number, sense: boolean): number {
  return f.add(`ORIENTED_EDGE('',*,*,#${edge},${sense ? '.T.' : '.F.'})`)
}

function edgeLoop(f: StepFile, orientedEdges: number[]): number {
  return f.add(`EDGE_LOOP('',(${orientedEdges.map((i) => `#${i}`).join(',')}))`)
}

// Bottom face loops traverse rings reversed relative to storage.
function bottomFaceLoop(f: StepFile, re: RingEntities): number {
  if (re.geom.kind === 'circle') {
    // Hole rings are stored CW; reversed = CCW = circle parameterization.
    // Outer circle stored CCW; reversed = .F.
    const sense = !re.geom.isOuter
    return edgeLoop(f, [orientedEdge(f, re.bottomCircleEdge, sense)])
  }
  const n = re.bottomEdges.length
  const oes: number[] = []
  for (let i = n - 1; i >= 0; i--) oes.push(orientedEdge(f, re.bottomEdges[i], false))
  return edgeLoop(f, oes)
}

// Top face loops traverse rings as stored.
function topFaceLoop(f: StepFile, re: RingEntities): number {
  if (re.geom.kind === 'circle') {
    // Hole stored CW = against circle parameterization = .F.; outer = .T.
    const sense = re.geom.isOuter
    return edgeLoop(f, [orientedEdge(f, re.topCircleEdge, sense)])
  }
  const oes = re.topEdges.map((e) => orientedEdge(f, e, true))
  return edgeLoop(f, oes)
}

function cylinderFace(f: StepFile, re: RingEntities): number {
  const geom = re.geom as RingCircle
  const { cx, cy, r } = geom.circle
  const surf = f.add(`CYLINDRICAL_SURFACE('',#${axis2Placement(f, cx, cy, 0)},${fmt(r)})`)
  // Edge senses pair against the plate faces (opposite use of each edge).
  const bottomSense = geom.isOuter // plate bottom used .F. for outer, .T. for hole reversal
  const topSense = !geom.isOuter
  const b1 = f.add(`FACE_BOUND('',#${edgeLoop(f, [orientedEdge(f, re.bottomCircleEdge, bottomSense)])},.T.)`)
  const b2 = f.add(`FACE_BOUND('',#${edgeLoop(f, [orientedEdge(f, re.topCircleEdge, topSense)])},.T.)`)
  // Hole bores face inward (toward the axis): same_sense .F.
  const sameSense = geom.isOuter ? '.T.' : '.F.'
  return f.add(`ADVANCED_FACE('',(#${b1},#${b2}),#${surf},${sameSense})`)
}

function sideFaces(f: StepFile, re: RingEntities, faces: number[]) {
  const geom = re.geom as RingPoly
  const pts = geom.pts
  const n = pts.length
  for (let i = 0; i < n; i++) {
    const [ax, ay] = pts[i]
    const [bx, by] = pts[(i + 1) % n]
    const ex = bx - ax
    const ey = by - ay
    const len = Math.hypot(ex, ey)
    if (len < 1e-12) continue
    // Outward normal: right of travel for outer CCW rings, and the same
    // formula points out of the material for hole CW rings.
    const nx = ey / len
    const ny = -ex / len
    const placement = axis2Placement(f, ax, ay, 0, nx, ny, 0, ex / len, ey / len, 0)
    const plane = f.add(`PLANE('',#${placement})`)
    const loop = edgeLoop(f, [
      orientedEdge(f, re.bottomEdges[i], true),
      orientedEdge(f, re.vertEdges[(i + 1) % n], true),
      orientedEdge(f, re.topEdges[i], false),
      orientedEdge(f, re.vertEdges[i], false),
    ])
    const bound = f.add(`FACE_OUTER_BOUND('',#${loop},.T.)`)
    faces.push(f.add(`ADVANCED_FACE('',(#${bound}),#${plane},.T.)`))
  }
}
